/**
 * Nhãn danh mục khiếu nại (packages/shared/src/claims.ts).
 *
 * Tiếng Anh: đúng chữ khách gửi 17/09/2026 ("After Service Terms for ERP",
 * "ClaimType 索偿类型"). Tiếng Trung loại lỗi: đúng mô tả khách gửi, bằng chữ giản thể
 * như file của khách (giao diện dùng zh-Hans từ 21/09/2026). Tiếng Việt, tiếng Trung
 * của nhóm lỗi và của hình thức xử lý: ToanKhonTech dịch — khách chưa gửi bản dịch.
 */
type IssueText = { label: string; desc: string };

export const claimsEn = {
  issue: "Issue type",
  category: "Issue group",
  severity: "Severity",
  severityFromIssue: "Set by the issue type",
  chooseIssue: "Choose an issue type",
  claims: "What the customer claims",
  claimsSupplier: "What we claim from the supplier",
  claimsHint: "Tick every claim that applies. Enter an amount only where money is claimed.",
  amount: "Amount",
  currency: "Currency",
  total: "Total claimed",
  rootCause: "Root cause",
  rootCauseNote: "Basis for the root cause",
  rootCauseNoteHint: "Required once a cause is chosen: what was found and how.",
  errors: {
    claim_issue: "Choose an issue type.",
    claim_type_not_allowed: "One of the claims does not apply to this issue type.",
    claim_type_duplicate: "The same claim is listed twice.",
    claim_amount: "Enter amounts as numbers with at most 2 decimals.",
    claim_amount_not_monetary: "An amount was entered for a claim that is not about money.",
    claim_record_only_exclusive: "“No claim, only record” cannot be combined with other claims.",
    claim_currency: "Choose the currency of the claimed amounts.",
    claim_severity: "Choose the severity for this issue type.",
    root_cause_note: "Describe the basis for the root cause (at least 5 characters).",
  },
  categories: {
    DELIVERY_DELAY: "Delivery delay",
    GOODS_DAMAGE: "Goods damage",
    NON_CONFORMING: "Non-conforming goods / wrong goods",
    SHORTAGE_OVER: "Shortage / over-shipment",
    PACKAGING_DEFECT: "Packaging defect",
    QUALITY_DEFECT: "Quality defect",
    DOCUMENTATION_ERROR: "Documentation error",
    OTHERS: "Others",
  },
  issues: {
    LATE_SHIPMENT: { label: "Late shipment", desc: "Goods shipped later than agreed schedule" },
    LATE_ARRIVAL: { label: "Late arrival", desc: "Goods arrived at destination later than agreed" },
    MISSED_SCHEDULE: { label: "Missed schedule", desc: "Failed to meet committed delivery timeline" },
    PARTIAL_DAMAGE: { label: "Partial damage", desc: "Partial goods damaged upon receipt" },
    TOTAL_DAMAGE: { label: "Total damage", desc: "Full consignment damaged and unusable" },
    TRANSIT_DAMAGE: { label: "Damage in transit", desc: "Damage occurred during transportation" },
    PACKAGING_DAMAGE: { label: "Damage due to poor packaging", desc: "Damage caused by inadequate packaging" },
    DIFFERENT_FROM_SAMPLE: { label: "Different from sample", desc: "Goods not matched with approved sample" },
    WRONG_SPECIFICATION: { label: "Wrong specification", desc: "Product specification does not match order" },
    WRONG_MATERIAL: { label: "Wrong material", desc: "Raw material different from agreed" },
    WRONG_COLOUR: { label: "Wrong colour", desc: "Colour not as per order requirement" },
    WRONG_SIZE: { label: "Wrong size", desc: "Dimension / size not as per order" },
    WRONG_ITEM: { label: "Wrong item / part no.", desc: "Item or part number mismatch" },
    SHORTAGE: { label: "Goods shortage", desc: "Received quantity less than ordered" },
    OVER_SHIPMENT: { label: "Over shipment", desc: "Received quantity more than ordered" },
    BROKEN_CARTON: { label: "Broken carton", desc: "Outer carton broken" },
    MISSING_LABEL: { label: "Missing label", desc: "Label / sticker missing" },
    INCORRECT_MARKING: { label: "Incorrect marking", desc: "Shipping mark incorrect" },
    SURFACE_DEFECT: { label: "Surface defect", desc: "Surface blemish, scratch, stain" },
    FUNCTIONAL_DEFECT: { label: "Functional defect", desc: "Product cannot function as required" },
    NON_COMPLIANT: { label: "Non-compliant with standard", desc: "Goods fail required test / standard" },
    WRONG_INVOICE: { label: "Wrong invoice", desc: "Invoice information incorrect" },
    WRONG_PACKING_LIST: { label: "Wrong packing list", desc: "Packing list data mismatch" },
    WRONG_CERTIFICATE: { label: "Wrong certificate", desc: "Test / compliance certificate incorrect" },
    OTHER: { label: "Other issue", desc: "Other unclassified goods related issue" },
  } satisfies Record<string, IssueText>,
  types: {
    RESERVE_CLAIM_RIGHT: "Reserve claim right",
    LIQUIDATED_DAMAGES: "Claim for liquidated damages",
    EXPEDITED_FREIGHT: "Claim for expedited freight",
    CONSEQUENTIAL_LOSS: "Claim for buyer’s consequential loss",
    REPLACEMENT_COST: "Claim replacement cost",
    REPAIR_COST: "Claim repair cost",
    SURVEY_FEE: "Claim survey / inspection fee",
    RETURN_FREIGHT: "Claim return freight",
    STORAGE_FEE: "Claim storage fee",
    SHORTAGE_VALUE: "Claim shortage value",
    REWORK_LOSS: "Claim rework loss",
    CUSTOMER_PENALTY: "Claim customer penalty",
    FULL_REFUND: "Request full refund",
    PARTIAL_REFUND: "Request partial refund",
    PRICE_DISCOUNT: "Request price discount",
    REPLACEMENT: "Request replacement",
    SUPPLEMENTARY_SHIPMENT: "Request supplementary shipment",
    GOODS_RETURN: "Request goods return",
    WARNING_NOTICE: "Warning notice",
    SUSPEND_ORDERS: "Suspend future orders",
    DEDUCT_RETURN_COST: "Deduct return cost",
    RECORD_ONLY: "No claim, only record",
    PRODUCTION_DOWNTIME: "Claim for production downtime loss",
    REPACK_COST: "Claim repack cost",
    REWORK: "Request rework",
    RELABEL: "Request re-labelling",
    REVISED_DOCUMENT: "Request revised document",
    REISSUE_DOCUMENT: "Reissue document",
    REISSUE_CERTIFICATE: "Reissue certificate",
    CASE_BY_CASE: "Custom claim as per case",
  },
  severities: { Critical: "Critical", Major: "Major", Minor: "Minor" },
  rootCauses: {
    Undetermined: "Not yet determined",
    Internal: "Internal (Hisense Button)",
    Supplier: "Supplier material or processing",
    Transport: "Transport / forwarder",
    Customer: "Customer handling or use",
  },
};

export const claimsVi: typeof claimsEn = {
  issue: "Loại lỗi",
  category: "Nhóm lỗi",
  severity: "Mức độ",
  severityFromIssue: "Theo loại lỗi",
  chooseIssue: "Chọn loại lỗi",
  claims: "Khách yêu cầu gì",
  claimsSupplier: "Mình đòi nhà cung cấp gì",
  claimsHint: "Tick mọi yêu cầu áp dụng. Chỉ nhập số tiền ở yêu cầu liên quan tới tiền.",
  amount: "Số tiền",
  currency: "Loại tiền",
  total: "Tổng yêu cầu",
  rootCause: "Nguyên nhân",
  rootCauseNote: "Căn cứ xác định nguyên nhân",
  rootCauseNoteHint: "Bắt buộc khi đã chọn nguyên nhân: phát hiện gì, kiểm bằng cách nào.",
  errors: {
    claim_issue: "Chọn loại lỗi.",
    claim_type_not_allowed: "Có yêu cầu không áp dụng cho loại lỗi này.",
    claim_type_duplicate: "Một yêu cầu bị chọn hai lần.",
    claim_amount: "Nhập số tiền dạng số, tối đa 2 chữ số lẻ.",
    claim_amount_not_monetary: "Đã nhập số tiền cho yêu cầu không liên quan tới tiền.",
    claim_record_only_exclusive: "“Không đòi, chỉ ghi nhận” không đi cùng yêu cầu khác.",
    claim_currency: "Chọn loại tiền cho số tiền yêu cầu.",
    claim_severity: "Chọn mức độ cho loại lỗi này.",
    root_cause_note: "Ghi căn cứ xác định nguyên nhân (ít nhất 5 ký tự).",
  },
  categories: {
    DELIVERY_DELAY: "Giao hàng trễ",
    GOODS_DAMAGE: "Hàng hư hỏng",
    NON_CONFORMING: "Hàng không đúng / sai hàng",
    SHORTAGE_OVER: "Thiếu / thừa hàng",
    PACKAGING_DEFECT: "Lỗi bao bì",
    QUALITY_DEFECT: "Lỗi chất lượng",
    DOCUMENTATION_ERROR: "Lỗi chứng từ",
    OTHERS: "Khác",
  },
  issues: {
    LATE_SHIPMENT: { label: "Xuất hàng trễ", desc: "Hàng xuất muộn hơn lịch đã thoả thuận" },
    LATE_ARRIVAL: { label: "Hàng về trễ", desc: "Hàng đến nơi muộn hơn thời hạn đã thoả thuận" },
    MISSED_SCHEDULE: { label: "Trễ tiến độ cam kết", desc: "Không đáp ứng tiến độ giao hàng đã cam kết" },
    PARTIAL_DAMAGE: { label: "Hỏng một phần", desc: "Một phần hàng bị hỏng khi nhận" },
    TOTAL_DAMAGE: { label: "Hỏng toàn bộ", desc: "Cả lô hàng hư hỏng, không dùng được" },
    TRANSIT_DAMAGE: { label: "Hỏng khi vận chuyển", desc: "Hư hỏng xảy ra trong quá trình vận chuyển" },
    PACKAGING_DAMAGE: { label: "Hỏng do đóng gói kém", desc: "Hư hỏng do đóng gói không đạt" },
    DIFFERENT_FROM_SAMPLE: { label: "Khác mẫu duyệt", desc: "Hàng không khớp mẫu đã duyệt" },
    WRONG_SPECIFICATION: { label: "Sai quy cách", desc: "Quy cách sản phẩm không đúng đơn hàng" },
    WRONG_MATERIAL: { label: "Sai vật liệu", desc: "Nguyên liệu khác với thoả thuận" },
    WRONG_COLOUR: { label: "Sai màu", desc: "Màu không đúng yêu cầu của đơn hàng" },
    WRONG_SIZE: { label: "Sai kích cỡ", desc: "Kích thước / cỡ không đúng đơn hàng" },
    WRONG_ITEM: { label: "Sai mã hàng", desc: "Mã hàng hoặc mã chi tiết không khớp" },
    SHORTAGE: { label: "Giao thiếu", desc: "Số lượng nhận ít hơn số lượng đặt" },
    OVER_SHIPMENT: { label: "Giao thừa", desc: "Số lượng nhận nhiều hơn số lượng đặt" },
    BROKEN_CARTON: { label: "Thùng vỡ", desc: "Thùng carton bên ngoài bị vỡ" },
    MISSING_LABEL: { label: "Thiếu nhãn", desc: "Thiếu nhãn / tem dán" },
    INCORRECT_MARKING: { label: "Sai shipping mark", desc: "Ký mã hiệu vận chuyển không đúng" },
    SURFACE_DEFECT: { label: "Lỗi bề mặt", desc: "Tì vết, trầy xước, ố bẩn trên bề mặt" },
    FUNCTIONAL_DEFECT: { label: "Lỗi chức năng", desc: "Sản phẩm không hoạt động như yêu cầu" },
    NON_COMPLIANT: { label: "Không đạt tiêu chuẩn", desc: "Hàng không đạt kiểm nghiệm / tiêu chuẩn yêu cầu" },
    WRONG_INVOICE: { label: "Sai hoá đơn", desc: "Thông tin hoá đơn không đúng" },
    WRONG_PACKING_LIST: { label: "Sai packing list", desc: "Số liệu packing list không khớp" },
    WRONG_CERTIFICATE: { label: "Sai chứng nhận", desc: "Chứng nhận kiểm nghiệm / tuân thủ không đúng" },
    OTHER: { label: "Vấn đề khác", desc: "Vấn đề khác về hàng hoá chưa phân loại" },
  },
  types: {
    RESERVE_CLAIM_RIGHT: "Bảo lưu quyền khiếu nại",
    LIQUIDATED_DAMAGES: "Đòi tiền phạt trễ hạn",
    EXPEDITED_FREIGHT: "Đòi cước vận chuyển gấp",
    CONSEQUENTIAL_LOSS: "Đòi thiệt hại phát sinh của bên mua",
    REPLACEMENT_COST: "Đòi chi phí thay hàng",
    REPAIR_COST: "Đòi chi phí sửa chữa",
    SURVEY_FEE: "Đòi phí giám định / kiểm tra",
    RETURN_FREIGHT: "Đòi cước trả hàng",
    STORAGE_FEE: "Đòi phí lưu kho",
    SHORTAGE_VALUE: "Đòi giá trị hàng thiếu",
    REWORK_LOSS: "Đòi thiệt hại làm lại",
    CUSTOMER_PENALTY: "Đòi lại tiền phạt của khách hàng",
    FULL_REFUND: "Yêu cầu hoàn tiền toàn bộ",
    PARTIAL_REFUND: "Yêu cầu hoàn tiền một phần",
    PRICE_DISCOUNT: "Yêu cầu giảm giá",
    REPLACEMENT: "Yêu cầu đổi hàng",
    SUPPLEMENTARY_SHIPMENT: "Yêu cầu giao bù",
    GOODS_RETURN: "Yêu cầu trả lại hàng",
    WARNING_NOTICE: "Gửi cảnh cáo",
    SUSPEND_ORDERS: "Tạm ngưng đặt hàng",
    DEDUCT_RETURN_COST: "Trừ chi phí trả hàng",
    RECORD_ONLY: "Không đòi, chỉ ghi nhận",
    PRODUCTION_DOWNTIME: "Đòi thiệt hại ngừng sản xuất",
    REPACK_COST: "Đòi chi phí đóng gói lại",
    REWORK: "Yêu cầu làm lại",
    RELABEL: "Yêu cầu dán lại nhãn",
    REVISED_DOCUMENT: "Yêu cầu sửa chứng từ",
    REISSUE_DOCUMENT: "Phát hành lại chứng từ",
    REISSUE_CERTIFICATE: "Cấp lại chứng nhận",
    CASE_BY_CASE: "Đòi theo từng trường hợp",
  },
  severities: { Critical: "Nghiêm trọng", Major: "Lớn", Minor: "Nhẹ" },
  rootCauses: {
    Undetermined: "Chưa xác định",
    Internal: "Lỗi nội bộ (Hisense Button)",
    Supplier: "Vật liệu / gia công của nhà cung cấp",
    Transport: "Vận chuyển / đơn vị giao nhận",
    Customer: "Do khách hàng sử dụng / bảo quản",
  },
};

export const claimsZh: typeof claimsEn = {
  issue: "问题类型",
  category: "问题类别",
  severity: "严重程度",
  severityFromIssue: "依问题类型设置",
  chooseIssue: "选择问题类型",
  claims: "客户索偿内容",
  claimsSupplier: "向供应商索偿内容",
  claimsHint: "勾选所有适用的索偿。仅在涉及金额的项目填写金额。",
  amount: "金额",
  currency: "币种",
  total: "索偿合计",
  rootCause: "根本原因",
  rootCauseNote: "判定原因的依据",
  rootCauseNoteHint: "选定原因后必填：发现了什么、如何查证。",
  errors: {
    claim_issue: "请选择问题类型。",
    claim_type_not_allowed: "有索偿项目不适用于此问题类型。",
    claim_type_duplicate: "同一索偿项目重复。",
    claim_amount: "金额请填数字，最多两位小数。",
    claim_amount_not_monetary: "非金额类的索偿项目填了金额。",
    claim_record_only_exclusive: "“不索偿，仅记录”不能与其他索偿同时选择。",
    claim_currency: "请选择索偿金额的币种。",
    claim_severity: "请为此问题类型选择严重程度。",
    root_cause_note: "请填写判定原因的依据（至少 5 个字）。",
  },
  categories: {
    DELIVERY_DELAY: "交货延迟",
    GOODS_DAMAGE: "货物损坏",
    NON_CONFORMING: "货物不符 / 错货",
    SHORTAGE_OVER: "短货 / 多发货",
    PACKAGING_DEFECT: "包装缺陷",
    QUALITY_DEFECT: "质量缺陷",
    DOCUMENTATION_ERROR: "单据错误",
    OTHERS: "其他",
  },
  issues: {
    LATE_SHIPMENT: { label: "出货延迟", desc: "出货晚于约定时程" },
    LATE_ARRIVAL: { label: "到货延迟", desc: "到达目的地晚于约定时间" },
    MISSED_SCHEDULE: { label: "未按计划交期", desc: "未能达成承诺的交货时程" },
    PARTIAL_DAMAGE: { label: "部分货物损坏", desc: "收货时部分货物损坏" },
    TOTAL_DAMAGE: { label: "整批货物损毁", desc: "整批货物损毁且无法使用" },
    TRANSIT_DAMAGE: { label: "运输途中破损", desc: "损坏发生于运输途中" },
    PACKAGING_DAMAGE: { label: "包装不良引致损坏", desc: "因包装不足导致损坏" },
    DIFFERENT_FROM_SAMPLE: { label: "与确认样品不符", desc: "货物与确认样品不符" },
    WRONG_SPECIFICATION: { label: "产品规格错误", desc: "产品规格与订单不符" },
    WRONG_MATERIAL: { label: "用料错误", desc: "原材料与约定不同" },
    WRONG_COLOUR: { label: "颜色错误", desc: "颜色不符合订单要求" },
    WRONG_SIZE: { label: "尺寸错误", desc: "尺寸 / 规格大小与订单不符" },
    WRONG_ITEM: { label: "货号错误", desc: "品项或料号不符" },
    SHORTAGE: { label: "短货", desc: "收货数量少于订购数量" },
    OVER_SHIPMENT: { label: "多发货", desc: "收货数量多于订购数量" },
    BROKEN_CARTON: { label: "外箱破损", desc: "外箱破损" },
    MISSING_LABEL: { label: "缺少标签", desc: "缺少标签 / 贴纸" },
    INCORRECT_MARKING: { label: "唛头错误", desc: "运输唛头错误" },
    SURFACE_DEFECT: { label: "表面瑕疵", desc: "表面瑕疵、刮痕、污渍" },
    FUNCTIONAL_DEFECT: { label: "功能不良", desc: "产品无法达到要求的功能" },
    NON_COMPLIANT: { label: "不符合标准", desc: "货物未通过要求的测试 / 标准" },
    WRONG_INVOICE: { label: "发票错误", desc: "发票信息错误" },
    WRONG_PACKING_LIST: { label: "装箱单错误", desc: "装箱单资料不符" },
    WRONG_CERTIFICATE: { label: "证书错误", desc: "测试 / 合规证书错误" },
    OTHER: { label: "其他未分类货物问题", desc: "其他未分类的货物相关问题" },
  },
  types: {
    RESERVE_CLAIM_RIGHT: "保留索偿权利",
    LIQUIDATED_DAMAGES: "索偿违约金",
    EXPEDITED_FREIGHT: "索偿加急运费",
    CONSEQUENTIAL_LOSS: "索偿买方间接损失",
    REPLACEMENT_COST: "索偿更换费用",
    REPAIR_COST: "索偿维修费用",
    SURVEY_FEE: "索偿公证 / 检验费",
    RETURN_FREIGHT: "索偿退货运费",
    STORAGE_FEE: "索偿仓储费",
    SHORTAGE_VALUE: "索偿短货货值",
    REWORK_LOSS: "索偿返工损失",
    CUSTOMER_PENALTY: "索偿客户罚款",
    FULL_REFUND: "要求全额退款",
    PARTIAL_REFUND: "要求部分退款",
    PRICE_DISCOUNT: "要求折价",
    REPLACEMENT: "要求换货",
    SUPPLEMENTARY_SHIPMENT: "要求补发货",
    GOODS_RETURN: "要求退回货物",
    WARNING_NOTICE: "发出警告通知",
    SUSPEND_ORDERS: "暂停后续订单",
    DEDUCT_RETURN_COST: "扣除退货费用",
    RECORD_ONLY: "不索偿，仅记录",
    PRODUCTION_DOWNTIME: "索偿停工损失",
    REPACK_COST: "索偿重新包装费用",
    REWORK: "要求返工",
    RELABEL: "要求重新贴标",
    REVISED_DOCUMENT: "要求修改单据",
    REISSUE_DOCUMENT: "重新开立单据",
    REISSUE_CERTIFICATE: "重新签发证书",
    CASE_BY_CASE: "视个案索偿",
  },
  severities: { Critical: "严重", Major: "主要", Minor: "轻微" },
  rootCauses: {
    Undetermined: "尚未确定",
    Internal: "内部原因（Hisense Button）",
    Supplier: "供应商材料或加工",
    Transport: "运输 / 货代",
    Customer: "客户使用或保管",
  },
};
